import { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { useNotification } from '@/_shared/hooks';
import { useWizard } from '@/_shared/hooks/useWizard';
import {
  CallResponsePostSurveysCampaignByIdNext,
  postSurveysCampaignByIdNextCall,
  postSurveysCampaignByIdEndCall,
} from '@/surveys/state/api';
import { QuestionType } from '@/swagger/constants/surveys-enums';

type UseEmployeeCompleteSurvey = {
  currentStep: (CallResponsePostSurveysCampaignByIdNext & { stepNumber: number }) | undefined;
  control: Control<FieldValues>;
};

export const useEmployeeCompleteSurvey: () => UseEmployeeCompleteSurvey = () => {
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { campaignId } = useParams();

  const {
    execute: executePostSurveysCampaignByIdNext,
    loading: loadingPostSurveysCampaignByIdNext,
    error: errorPostSurveysCampaignByIdNext,
  } = postSurveysCampaignByIdNextCall.hook();

  const { execute: executePostSurveysCampaignByIdEnd, loading: loadingPostSurveysCampaignByIdEnd } =
    postSurveysCampaignByIdEndCall.hook();

  const [currentStep, setCurrentStep] =
    useState<UseEmployeeCompleteSurvey['currentStep']>(undefined);

  const [progressiveSteps, setProgressiveSteps] = useState<
    { [step: number]: CallResponsePostSurveysCampaignByIdNext } | undefined
  >(undefined);

  const {
    setCustomOnNext,
    setCustomOnPrevious,
    setNavigationButtonStatus,
    resetWizardNavigationCallbacks,
    resetWizardNavigationButtonStatus,
  } = useWizard();

  const { control, watch } = useForm<FieldValues>({
    defaultValues: {},
  });

  const dynamicData = watch();

  const isNextButtonDisabled = useMemo(() => {
    if (errorPostSurveysCampaignByIdNext) {
      return true;
    }
    if (!currentStep) {
      return false;
    }
    if (currentStep.nextQuestion?.isRequired) {
      const questionId = currentStep.nextQuestion?.id || '';

      if (
        currentStep.nextQuestion.type === QuestionType.SINGLE_CHOICE ||
        currentStep.nextQuestion.type === QuestionType.DIFFERENTIAL
      ) {
        return !(dynamicData as FieldValues)[questionId];
      }
      if (currentStep.nextQuestion.type === QuestionType.MULTIPLE_CHOICE) {
        const someDataFilled = Object.keys(dynamicData).some(
          key =>
            key.startsWith(`multipleChoiceAnswer_${questionId}_`) &&
            (dynamicData as FieldValues)[key],
        );
        return !someDataFilled;
      }
    }
    return false;
  }, [currentStep, dynamicData, errorPostSurveysCampaignByIdNext]);

  const onSubmit: NonNullable<Parameters<typeof setCustomOnNext>[0]> = useCallback(
    navigate => {
      if (!currentStep) {
        return;
      }
      // Don't use dynamicData directly to avoid loop crash memoization.
      // ensure we have the latest values
      const data = watch();

      let selectedOptionsIds: string[] | undefined;
      let differentialValue: number | undefined;
      const questionId = currentStep.nextQuestion?.id || '';
      if (
        currentStep.nextQuestion?.type === QuestionType.SINGLE_CHOICE ||
        currentStep.nextQuestion?.type === QuestionType.MULTIPLE_CHOICE
      ) {
        if (currentStep.nextQuestion?.type === QuestionType.SINGLE_CHOICE) {
          selectedOptionsIds = [(data as FieldValues)[questionId] as string];
        } else {
          // MULTIPLE_CHOICE
          selectedOptionsIds = Object.keys(data)
            .filter(
              key =>
                key.startsWith(`multipleChoiceAnswer_${questionId}_`) && (data as FieldValues)[key],
            )
            .map(key => key.replace(`multipleChoiceAnswer_${questionId}_`, ''));
        }
      }
      if (currentStep.nextQuestion?.type === QuestionType.DIFFERENTIAL) {
        const differentialFormValue = (data as FieldValues)[questionId] as string;
        differentialValue =
          parseFloat(`${differentialFormValue ?? ''}`) || Number(`${differentialFormValue ?? ''}`);
      }

      executePostSurveysCampaignByIdNext({
        id: campaignId || '',
        currentStep: currentStep.stepNumber + 1,
        answer: {
          questionId,
          selectedOptionIds: selectedOptionsIds,
          differentialValue,
        },
        successSaga: ({ response }) => {
          if (response.isLast) {
            executePostSurveysCampaignByIdEnd({
              id: campaignId || '',
              successSaga: () => {
                navigate?.();
              },
              errorSaga: () => {
                notify(tShared('errors.genericApiError'), 'error');
              },
            });
          } else {
            setCurrentStep({ ...response, stepNumber: currentStep.stepNumber + 1 });
            // Store the step in progressiveSteps
            setProgressiveSteps(prev => ({ ...prev, [currentStep.stepNumber + 1]: response }));
          }
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentStep,
      executePostSurveysCampaignByIdNext,
      executePostSurveysCampaignByIdEnd,
      campaignId,
      watch,
      setCurrentStep,
    ],
  );

  const onPrevious: NonNullable<Parameters<typeof setCustomOnPrevious>[0]> = useCallback(
    navigate => {
      // If there's no current step or it's the first step, use wizard navigate
      if (!currentStep || !currentStep.stepNumber) {
        navigate?.();
        return;
      }
      // Remove the current step from progressiveSteps
      delete progressiveSteps?.[currentStep.stepNumber];
      setCurrentStep({
        ...progressiveSteps?.[currentStep.stepNumber - 1],
        stepNumber: currentStep.stepNumber - 1,
      });
      setProgressiveSteps({ ...progressiveSteps });
    },
    [currentStep, progressiveSteps],
  );

  useEffect(() => {
    executePostSurveysCampaignByIdNext({
      id: campaignId || '',
      currentStep: 0,
      successSaga: ({ response }) => {
        setCurrentStep({ ...response, stepNumber: 0 });
        // Store the first step in progressiveSteps
        setProgressiveSteps({ 0: response });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCustomOnNext(navigate => onSubmit(navigate));
    setCustomOnPrevious(navigate => onPrevious(navigate));

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
  }, [onPrevious, onSubmit, resetWizardNavigationCallbacks, setCustomOnNext, setCustomOnPrevious]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: isNextButtonDisabled,
      isLoadingNextButton: loadingPostSurveysCampaignByIdNext || loadingPostSurveysCampaignByIdEnd,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [
    isNextButtonDisabled,
    loadingPostSurveysCampaignByIdNext,
    loadingPostSurveysCampaignByIdEnd,
    resetWizardNavigationButtonStatus,
    setNavigationButtonStatus,
  ]);

  return {
    currentStep,
    control,
  };
};
